import { useQuery } from '@tanstack/react-query';
import { Plus, ShieldAlert } from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';
import { Button, Card, EmptyState, PageHeader, Skeleton } from '../../components/ui';
import { useSetCrumbs } from '../../lib/crumbs';
import { ApiError, reportClientError } from '../../lib/errors';
import { useRole } from '../../lib/role';
import type { AuthedPageProps } from '../../page-route';
import { AgentsSection } from './Agents';
import { AgentsCard } from './AgentsCard';
import { ComputersCard } from './Computers';
import { ConnectDialog } from './ConnectDialog';
import { MySamples } from './MySamples';
import { SAMPLE_ROLES, myAgents } from './api';
import { ErrorCode, LoadError, shouldReport } from './parts';
import { LABEL, PC_LABEL, PC_T, T, TITLE } from './texts';
import { useComputers } from './useComputers';

/*
 * «Мои ИИ-клиенты» `/my-agents` (005, T059, T147; spec.md, US7; contracts/http-api.md; FR-060; 006, T041, T042, T055;
 * specs/006-agent-client/contracts/ui.md §1). Порядок страницы:
 * - заголовок с главной кнопкой «Подключить компьютер» (006) — окно `ConnectDialog.tsx`;
 * - «Компьютеры» (006, `Computers.tsx`) — свои компьютеры с коннектором, «Показать агентов», «Отключить»;
 * - «Агенты на <компьютер>» (006, `AgentsCard.tsx`) — агенты выбранного компьютера, одно состояние и одна кнопка;
 * - «Подключения агентов к табелю» (005, `Agents.tsx`) — из ответа `GET /api/me/agents` (`agents`): подключения
 *   ИИ-клиентов, права, дата, последний вызов, состояние, «Отозвать» с подтверждением;
 * - «Мои выборки» (T140, `MySamples.tsx`) — свой запрос `GET /api/me/samples`, у ролей строки «Выборки дней».
 * Разделы 006 — свой адрес `GET /api/agent/connectors` (`useComputers.ts`): у ролей без права, без права «ИИ-агент»
 * (`403`) и при выключенной функции 006 (`404`) их нет, и кнопки «Подключить компьютер» нет. Разделы 005 при этом не
 * меняются.
 * - Состояния ответа 005 — общие для его разделов: загрузка, ошибка с кодом и «Повторить», ответ не по схеме, роли
 *   нельзя подключать агентов (`403`).
 * - Один интерфейс на 375/768/1280 px: разделы — друг под другом на любой ширине.
 */

const SCREEN = 'MyAgents';

const isForbidden = (e: unknown): e is ApiError => e instanceof ApiError && e.status === 403;

export function MyAgentsPage({ me }: AuthedPageProps) {
  useSetCrumbs([{ label: TITLE.page }]);
  const { role } = useRole();
  const tz = me.object.tzOffset;
  // Ответ зависит от роли (право `me.agents`): смена роли — запрос заново.
  const list = useQuery({ queryKey: ['my-agents', role], queryFn: myAgents });
  const pcs = useComputers();
  const [connecting, setConnecting] = useState(false);
  /** Растёт — фокус на заголовок карточки «Агенты на …»: человек выбрал компьютер. */
  const [agentsFocus, setAgentsFocus] = useState(0);
  const showAgents = (id: number) => {
    pcs.select(id);
    setAgentsFocus((t) => t + 1);
  };

  useEffect(() => {
    if (list.error && shouldReport(list.error)) reportClientError(SCREEN, 'load', list.error);
  }, [list.error]);

  let sections: ReactNode;
  if (list.isError) {
    sections = isForbidden(list.error) ? (
      <Forbidden error={list.error} />
    ) : (
      <LoadError
        title={TITLE.loadError}
        error={list.error}
        busy={list.isFetching}
        onRetry={() => void list.refetch()}
      />
    );
  } else if (!list.data) sections = <Loading />;
  else sections = <AgentsSection agents={list.data.agents} tz={tz} reload={list.refetch} />;

  return (
    <>
      <PageHeader
        title={TITLE.page}
        description={pcs.available ? PC_T.intro : T.intro}
        actions={
          pcs.ready ? (
            <Button variant="primary" icon={Plus} onClick={() => setConnecting(true)}>
              {PC_LABEL.connect}
            </Button>
          ) : undefined
        }
      />
      {pcs.available && (
        <ComputersCard
          list={pcs.list}
          error={pcs.error}
          busy={pcs.busy}
          onRetry={() => void pcs.refetch()}
          selectedId={pcs.selected?.id ?? null}
          onSelect={showAgents}
          tz={tz}
          reload={pcs.refetch}
        />
      )}
      {pcs.selected && (
        <AgentsCard
          key={pcs.selected.id}
          computer={pcs.selected}
          tz={tz}
          onRefresh={() => void pcs.refetch()}
          focusTick={agentsFocus}
        />
      )}
      {sections}
      {/* «Мои выборки» (T140): у ролей строки «Выборки дней»; свой запрос — подключения его не ждут. */}
      {SAMPLE_ROLES.includes(role) && <MySamples tz={tz} />}
      {pcs.ready && (
        <ConnectDialog
          open={connecting}
          onClose={() => setConnecting(false)}
          onDone={(id) => {
            setConnecting(false);
            showAgents(id);
          }}
        />
      )}
    </>
  );
}

/* ---------- Состояния ответа ---------- */

function Loading() {
  return (
    <div aria-busy="true" aria-label={LABEL.loading}>
      <Skeleton className="mb-3 h-5 w-32" />
      <Card className="divide-y divide-line">
        {[0, 1].map((i) => (
          <div key={i} className="flex gap-3 px-6 py-4 max-sm:px-4">
            <Skeleton className="h-9 w-9 shrink-0" />
            <div className="min-w-0 flex-1 space-y-2">
              <Skeleton className="h-4 w-2/5" />
              <Skeleton className="h-3 w-1/4" />
              <Skeleton className="mt-3 h-4 w-4/5" />
            </div>
          </div>
        ))}
      </Card>
    </div>
  );
}

/** `403`: в этой роли подключать агентов нельзя (право `me.agents`). Повтор ничего не даст — кнопки нет. */
function Forbidden({ error }: { error: ApiError }) {
  return (
    <Card>
      <EmptyState
        icon={ShieldAlert}
        title={TITLE.forbidden}
        description={T.forbidden}
        action={
          <div className="flex flex-col items-center gap-2">
            <p className="text-[13px] text-ink-2">{T.forbiddenFix}</p>
            <ErrorCode error={error} />
          </div>
        }
      />
    </Card>
  );
}
